import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from '@/lib/toast';
import { buildPublicProfileUrl } from '@/lib/inviteLinks';
import { Button } from '@/components/Button';
import { CopyIcon, CheckIcon, ChevronDownIcon } from '@/components/icons';

/**
 * "Мой QR-код" section for the profile settings tab — the web counterpart of
 * the mobile QrCardScreen. The payload is the public `…/u/<name>` landing
 * page, so any phone camera resolves it without an in-app scanner. Dark
 * modules on a white panel scan reliably regardless of the app theme.
 */
export function ProfileQrCard({ username }: { username: string }) {
  const { t } = useTranslation();
  const url = buildPublicProfileUrl(username);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      // Flip back to "copy" after a beat. No stored timer: a stale flag is
      // harmless and the modal unmounts the node anyway.
      window.setTimeout(() => setCopied(false), 1600);
      toast.success(t('prof.settings.qr.copied'));
    } catch {
      toast.error(t('prof.settings.qr.copyError'));
    }
  };

  return (
    <details className="app-settings-profile-qr">
      <summary className="app-settings-profile-qr-toggle">
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-ink">{t('prof.settings.qr.title')}</span>
          <span className="block text-xs text-ink-muted">{t('prof.settings.qr.hint')}</span>
        </span>
        <ChevronDownIcon className="app-settings-profile-qr-chevron size-4 shrink-0 text-ink-muted" />
      </summary>
      <div className="app-settings-profile-qr-content flex flex-col items-center gap-4 sm:flex-row sm:items-center">
        {/* White QR panel — kept light independent of theme so the contrast
            stays scan-safe. */}
        <div className="shrink-0 rounded-xl bg-white p-3">
          <QRCodeSVG value={url} size={132} level="M" marginSize={0} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-center gap-3 sm:items-start">
          <div className="min-w-0 text-center sm:text-left">
            <div className="truncate font-medium text-ink">@{username}</div>
            <div className="truncate text-xs text-ink-muted">
              {url.replace(/^https?:\/\//, '')}
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => void copy()}
            leftIcon={
              copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />
            }
          >
            {copied ? t('prof.settings.qr.copied') : t('prof.settings.qr.copyLink')}
          </Button>
        </div>
      </div>
    </details>
  );
}
